import { useCallback, useRef, useState } from 'react';
import { useApiGetter, useApiEffect, AdminService } from '@localstack/services';
import { ButtonWithDialog, ProgressButton } from '@localstack/ui';
import {
  Stack,
  TextField,
  Box,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Typography,
  Avatar,
  CardHeader,
  CardContent,
  Card,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { ADMIN_PERMISSION_GROUPS } from '@localstack/constants';
import { AdminPermission, AdminUser } from '@localstack/types';

import { AdminLayout } from '~/layouts';
import { useAuthProvider } from '~/hooks/useAuthProvider';

import { AdminUserRow } from './AdminUserRow';
import { PermissionCheckboxes } from './PermissionCheckboxes';

export const StyledMemberTable = styled(Table)(({ theme }) => ({
  '& > *': {
    textWrap: 'nowrap',
  },
  '& tbody tr:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& tr td': {
    padding: theme.spacing(1),
    paddingLeft: 0,
    paddingRight: 0,
  },
  '& tr:hover': {
    cursor: 'pointer',
  },
}));

export const NoHoverTableRow = styled(TableRow)(() => ({
  '&:hover': {
    backgroundColor: 'unset !important',
    cursor: 'unset !important',
  },
}));

export const DropdownTableCell = styled(TableCell)(() => ({
  width: '2em',
}));

export const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: '2em',
  height: '2em',
  display: 'inline-flex',
  marginRight: '0.5em',
  border: `1px solid ${theme.palette.divider}`,
}));

export const InlineChip = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '999px',
  textWrap: 'nowrap',
  color: 'black',
  fontSize: '0.95em',
  padding: '0.28em 0.56em',
  lineHeight: 1.2,
  marginLeft: '0.6em',
  minHeight: '1.4em',
}));

export const SuperAdminChip = styled(InlineChip)(({ theme }) => ({
  backgroundColor: theme.palette.info.main,
  color: theme.palette.common.white,
}));

export const YouChip = styled(InlineChip)(({ theme }) => ({
  backgroundColor: theme.palette.info.main,
  color: theme.palette.common.white,
}));

export const SkeletonUser = styled(Skeleton)(() => ({
  width: '20em',
  height: '2em',
  transform: 'unset',
}));

export const AdminUsers = (): JSX.Element => {
  const { userInfo } = useAuthProvider();
  const [newAdminEmail, setNewAdminEmail] = useState<string>('');
  const [activeAdmin, setActiveAdmin] = useState<AdminUser | null>(null);

  const { data: adminUsers, isLoading: isListAdminUsersLoading } = useApiGetter(AdminService, 'listAdminUsers', []);
  const {
    addAdminUser,
    removeAdminUser,
    updateAdminUserPermissions,
    isLoading: isAdminsUpdatingLoading,
  } = useApiEffect(AdminService, ['addAdminUser', 'removeAdminUser', 'updateAdminUserPermissions'], {
    revalidate: ['listAdminUsers'],
  });

  const activeRowRef = useRef<HTMLTableRowElement | null>(null);

  const toggleActiveAdminUser = (adminUser: AdminUser) => {
    const isCurrentlyActive = activeAdmin?.id === adminUser.id;
    setActiveAdmin(
      isCurrentlyActive
        ? null
        : {
            id: adminUser.id,
            email: adminUser.email,
            permissions: adminUser.permissions ?? [],
          },
    );
    if (!isCurrentlyActive) {
      setTimeout(() => {
        activeRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const togglePermission = useCallback(
    (permission: AdminPermission) => {
      if (!activeAdmin) return;

      const isChecked = activeAdmin.permissions.includes(permission);

      const updatePermissions = (newPermissions: AdminPermission[]) => {
        setActiveAdmin({ ...activeAdmin, permissions: newPermissions });
      };

      if (permission === AdminPermission.SUPER_ADMIN) {
        return isChecked
          ? updatePermissions(activeAdmin.permissions.filter((p) => p !== AdminPermission.SUPER_ADMIN))
          : updatePermissions(Array.from(new Set(Object.values(ADMIN_PERMISSION_GROUPS).flat())));
      }

      if (!isChecked && permission.includes(':write')) {
        const readPermission = permission.replace(':write', ':read') as AdminPermission;
        return updatePermissions(Array.from(new Set([...activeAdmin.permissions, readPermission, permission])));
      }

      updatePermissions(
        isChecked ? activeAdmin.permissions.filter((p) => p !== permission) : [...activeAdmin.permissions, permission],
      );
    },
    [activeAdmin],
  );

  const handleSavePermissions = async () => {
    if (!activeAdmin) return;
    await updateAdminUserPermissions(activeAdmin.id, {
      permissions: activeAdmin.permissions,
    });
    setActiveAdmin(null);
  };

  return (
    <AdminLayout title="Admin Users">
      <Box sx={{ minHeight: '100vh', overflowY: 'visible' }}>
        <Typography variant="h6" gutterBottom>
          Grant or revoke access to the admin panel by adding or removing localstack.cloud emails.
        </Typography>
        <Box mt={2} mb={2}>
          <Stack direction="row" spacing={1}>
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Admin Email"
              size="small"
              type="email"
              value={newAdminEmail}
              onChange={(e) => setNewAdminEmail(e.target.value ?? '')}
            />
            <ProgressButton
              color="primary"
              variant="contained"
              loading={isAdminsUpdatingLoading}
              onClick={async () => {
                try {
                  const user = await addAdminUser({ email: newAdminEmail });
                  if (user) {
                    setNewAdminEmail('');
                  }
                } catch (err) {
                  console.error('Failed to add admin user:', err);
                }
              }}
              disabled={!newAdminEmail}
            >
              Add
            </ProgressButton>
          </Stack>
        </Box>
        <Grid item xs={12}>
          <StyledMemberTable>
            {(isListAdminUsersLoading || (adminUsers && adminUsers.length > 0)) && (
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {isListAdminUsersLoading ? (
                [...Array(3)].map((_, i) => (
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  <TableRow key={i}>
                    <TableCell colSpan={3}>
                      <SkeletonUser />
                    </TableCell>
                  </TableRow>
                ))
              ) : adminUsers?.length === 0 ? (
                <TableRow hover={false}>
                  <TableCell colSpan={3} align="center">
                    <Typography color="textSecondary">No admin users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                adminUsers &&
                adminUsers.length > 0 &&
                adminUsers.map((adminUser) => {
                  const isActive = activeAdmin?.id === adminUser.id;
                  const isSelf = adminUser.email === userInfo?.user.email;
                  const currentPermissions = isActive ? activeAdmin.permissions : adminUser.permissions;
                  const originalPermissions = adminUser.permissions;

                  const hasChanges =
                    isActive &&
                    !isSelf &&
                    (currentPermissions.length !== originalPermissions.length ||
                      currentPermissions.some((p) => !originalPermissions.includes(p)));

                  return (
                    <AdminUserRow
                      key={adminUser.id}
                      adminUser={adminUser}
                      isActive={isActive}
                      isSelf={isSelf}
                      onToggle={() => toggleActiveAdminUser(adminUser)}
                      activeRowRef={activeRowRef}
                    >
                      <Box mt={1} mb={1}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Card variant="outlined">
                              <CardHeader title="Permissions" />
                              <CardContent sx={{ mt: 0, pt: 0 }}>
                                <PermissionCheckboxes
                                  currentPermissions={currentPermissions}
                                  isSelf={isSelf}
                                  togglePermission={togglePermission}
                                />
                                <Box mt={2} textAlign="right">
                                  <ProgressButton
                                    color="primary"
                                    variant="contained"
                                    loading={isAdminsUpdatingLoading}
                                    disabled={!hasChanges}
                                    onClick={handleSavePermissions}
                                  >
                                    Save
                                  </ProgressButton>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={12}>
                            <Card variant="outlined">
                              <CardHeader title="Actions" />
                              <CardContent>
                                <ButtonWithDialog
                                  componentType="Button"
                                  color="error"
                                  disabled={isAdminsUpdatingLoading || isSelf}
                                  variant={'contained'}
                                  dialogTitle={`Remove admin user ${adminUser.email}?`}
                                  dialogContent={`Are you sure you want to revoke access to the admin panel for ${adminUser.email}?`}
                                  confirmButtonText="Remove"
                                  confirmButtonProps={{ color: 'error' }}
                                  onClick={() => removeAdminUser(adminUser.id)}
                                >
                                  Remove From Admin Panel
                                </ButtonWithDialog>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>
                    </AdminUserRow>
                  );
                })
              )}
            </TableBody>
          </StyledMemberTable>
        </Grid>
      </Box>
    </AdminLayout>
  );
};
